import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, deleteDoc, addDoc, serverTimestamp, getDocs } from 'firebase/firestore';

export default function WeddingDashboard({ user, navigateTo, db }) {
    const [websites, setWebsites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [siteToDelete, setSiteToDelete] = useState(null); 

    useEffect(() => {
        if (!user) return;
        const q = query(collection(db, "weddings"), where("ownerId", "==", user.uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setWebsites(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, [user, db]);

    const createNewWebsite = async () => {
        if (!user) return;
        const newSiteRef = await addDoc(collection(db, "weddings"), {
            ownerId: user.uid,
            title: "Our New Wedding",
            subdomain: '',
            isPublished: false,
            createdAt: serverTimestamp(),
            theme: { primaryColor: "#C08497", fontFamily: "Georgia, serif", backgroundColor: "#fdfcfa" },
            sections: [
                { id: 'hero', type: 'hero', enabled: true, content: { title: 'We\'re Getting Married!', date: new Date().toISOString().split('T')[0], city: '', imageUrl: '[https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1920&q=80](https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1920&q=80)' } },
                { id: 'story', type: 'story', enabled: true, content: { heading: 'Our Story', text: 'We met, we fell in love, and now we are tying the knot! We cannot wait to celebrate the beginning of our new chapter with all of our favorite people.', imageUrl: '' } },
                { id: 'events', type: 'events', enabled: true, content: { heading: 'Schedule of Events', eventsList: [{ title: 'Ceremony', time: '4:00 PM', location: 'The Grand Gardens', description: 'Please arrive 15 minutes early to find your seat.' }, { title: 'Reception', time: '5:30 PM', location: 'Main Ballroom', description: 'Dinner, drinks, and dancing to follow!' }] } },
                { id: 'travel', type: 'travel', enabled: true, content: { heading: 'Travel & Accommodations', accommodations: [{ name: 'The Plaza Hotel', address: '123 Main St, City, ST', details: 'Mention our wedding for a discounted room block rate.', bookingUrl: '' }] } },
                { id: 'registry', type: 'registry', enabled: true, content: { heading: 'Gift Registry', links: [{ storeName: 'Target', url: '[https://target.com](https://target.com)' }, { storeName: 'Zola', url: '[https://zola.com](https://zola.com)' }] } },
                { id: 'gallery', type: 'gallery', enabled: true, content: { heading: 'Photo Gallery', images: [] } },
                { id: 'rsvp', type: 'rsvp', enabled: true, content: { heading: 'Will You Be There?' } },
            ]
        });
        navigateTo('weddingSiteEditor', { weddingId: newSiteRef.id });
    };

    const handleDeleteWebsite = async () => {
        if (!siteToDelete) return;
        try {
            await deleteDoc(doc(db, "weddings", siteToDelete));
            setSiteToDelete(null);
        } catch (error) {
            console.error("Error deleting wedding site:", error);
        }
    };

    const handleExportAddresses = async (weddingId, weddingTitle) => {
        try {
            const q = query(collection(db, `weddings/${weddingId}/saveTheDates`));
            const snap = await getDocs(q);
            if (snap.empty) {
                alert("No addresses collected yet.");
                return;
            }
            
            const data = snap.docs.map(d => d.data()).sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0));
            
            const headers = "First Name,Last Name,Email,Phone,Additional Guests,Address 1,Address 2,City,State,Zip,Country\n";
            const csv = headers + data.map(r => `"${r.firstName || ''}","${r.lastName || ''}","${r.email || ''}","${r.phone || ''}","${r.additionalGuests || 0}","${r.address1 || ''}","${r.address2 || ''}","${r.city || ''}","${r.state || ''}","${r.zip || ''}","${r.country || ''}"`).join("\n");
            
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.setAttribute("download", `${weddingTitle.replace(/\s+/g, '_')}_Addresses.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error exporting addresses:", error);
            alert("Failed to export addresses.");
        }
    };

    if (loading) return <p>Loading your websites...</p>;

    return (
        <div>
            {siteToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4 animate-fade-in">
                    <div className="bg-white p-6 rounded-xl shadow-xl max-w-sm w-full">
                        <h2 className="text-xl font-bold mb-4">Delete Website?</h2>
                        <p className="mb-6 text-gray-600 text-sm">Are you sure you want to delete this wedding website? This action cannot be undone.</p>
                        <div className="flex justify-end space-x-3">
                            <button onClick={() => setSiteToDelete(null)} className="px-4 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 font-semibold text-sm transition">Cancel</button>
                            <button onClick={handleDeleteWebsite} className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 font-semibold text-sm transition">Delete</button>
                        </div>
                    </div>
                </div>
            )}
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">My Wedding Websites</h3>
                <button onClick={createNewWebsite} className="bg-pink-500 text-white px-4 py-2 rounded-lg hover:bg-pink-600">
                    + Create New Site
                </button>
            </div>
            <div className="space-y-4">
                {websites.length > 0 ? websites.map(site => (
                    <div key={site.id} className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:border-pink-300 hover:shadow-md transition-all duration-300 flex flex-col md:flex-row gap-5 items-start md:items-center group">
                        {(() => {
                            const heroSection = site.sections?.find(s => s.type === 'hero');
                            const imgUrl = heroSection?.content?.imageUrl || '[https://placehold.co/400x300/fdf2f8/db2777?text=Wedding](https://placehold.co/400x300/fdf2f8/db2777?text=Wedding)';
                            return (
                                <div className="w-full md:w-48 h-32 rounded-xl overflow-hidden flex-shrink-0 relative shadow-sm border border-gray-100">
                                    <img src={imgUrl} alt={site.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
                                    <div className="absolute bottom-2 left-2 flex gap-1">
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider shadow-sm ${site.isPublished ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-gray-100 text-gray-600 border-gray-300'}`}>
                                            {site.isPublished ? 'Live' : 'Draft'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })()}
                        
                        <div className="flex-grow min-w-0">
                            <h4 className="font-bold text-xl text-gray-900 mb-1 truncate">{site.title}</h4>
                            {site.subdomain ? (
                                <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-3">
                                    <span>🌐</span>
                                    <a href={`/w/${site.subdomain}`} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:underline truncate font-medium">
                                        {`${window.location.host}/w/${site.subdomain}`}
                                    </a>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400 italic mb-3">No custom URL set yet.</p>
                            )}
                            <p className="text-xs text-gray-400">Created: {site.createdAt?.seconds ? new Date(site.createdAt.seconds * 1000).toLocaleDateString() : 'Recently'}</p>
                        </div>

                        <div className="flex flex-wrap lg:flex-row items-center justify-end gap-2 w-full md:w-auto flex-shrink-0">
                            <button onClick={() => navigateTo('weddingSiteEditor', { weddingId: site.id })} className="flex-1 md:flex-none bg-gray-900 hover:bg-black text-white px-5 py-2.5 rounded-xl text-sm font-bold transition shadow-sm whitespace-nowrap text-center">
                                Edit & Manage
                            </button>
                            <button onClick={() => handleExportAddresses(site.id, site.title)} className="flex-1 md:flex-none bg-purple-50 text-purple-700 hover:bg-purple-100 px-4 py-2.5 rounded-xl text-sm font-bold transition shadow-sm whitespace-nowrap text-center">
                                Export List
                            </button>
                            <button onClick={() => setSiteToDelete(site.id)} className="p-2.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl transition" title="Delete Site">
                                <svg xmlns="[http://www.w3.org/2000/svg](http://www.w3.org/2000/svg)" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                        </div>
                    </div>
                )) : (
                    <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-200">
                        <div className="text-5xl mb-4">💍</div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">No websites created</h3>
                        <p className="text-gray-500 mb-6 max-w-sm mx-auto">Create a beautiful wedding hub for your guests with RSVPs, details, and more.</p>
                        <button onClick={createNewWebsite} className="bg-pink-600 text-white px-6 py-2.5 rounded-full font-bold hover:bg-pink-700 transition shadow-md">
                            Create First Site
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}